import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Heart, Lock, Globe, User, Mail, UserCircle, CheckCircle2, XCircle, Eye, EyeOff, Phone } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import Brand from "@/components/Brand";

const SYMBOLS_REGEX = /[!@#$%^&*()_+\-=[\]{};:,.<>?/|~]/;

function passwordChecks(pw) {
  return {
    length: pw.length >= 8,
    letter: /[A-Za-z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: SYMBOLS_REGEX.test(pw),
  };
}

function strengthScore(checks) {
  return Object.values(checks).filter(Boolean).length;
}

function Check({ ok, label, testid }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-700" : "text-gray-500"}`} data-testid={testid}>
      {ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {label}
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const { t, lang, setLang } = useT();
  const { register } = useAuth();
  const [form, setForm] = useState({ username: "", password: "", password_confirm: "", email: "", full_name: "", phone: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successInfo, setSuccessInfo] = useState(null); // { email, sent, pending }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const checks = useMemo(() => passwordChecks(form.password), [form.password]);
  const pwMatches = form.password && form.password === form.password_confirm;
  const score = strengthScore(checks);

  const canSubmit = form.username && form.email && form.password && pwMatches && score === 4 && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await register({
        username: form.username.trim().toLowerCase(),
        password: form.password,
        password_confirm: form.password_confirm,
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
      });
      setSuccessInfo({ email: form.email, sent: res.verification_email_sent, pending: res.pending_approval });
      setTimeout(() => navigate(res.pending_approval ? "/login" : "/"), 4500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("register_error_generic"));
    } finally {
      setLoading(false);
    }
  };

  if (successInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pattern px-4">
        <div className="bg-white rounded-2xl card-shadow p-8 max-w-md w-full text-center" data-testid="register-success">
          <Brand variant="hero" className="mb-4" />
          <div className={`${successInfo.pending ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"} rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4`}>
            <CheckCircle2 size={32} />
          </div>
          <h2 className={`text-xl font-bold mb-2 ${successInfo.pending ? "text-amber-800" : "text-emerald-800"}`}>
            {successInfo.pending ? t("register_pending_title") : t("register_success_title")}
          </h2>
          <p className="text-gray-600 text-sm mb-4" data-testid="register-pending-msg">
            {successInfo.pending
              ? t("register_pending_msg")
              : (successInfo.sent
                  ? t("register_success_email_sent").replace("{email}", successInfo.email)
                  : t("register_success_no_email"))}
          </p>
          <p className="text-xs text-gray-400">{t("redirecting")}…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-pattern px-4 py-6" data-testid="register-page">
      {/* Prominent language toggle — top corner */}
      <button
        onClick={() => setLang(lang === "ar" ? "en" : "ar")}
        className="fixed top-4 end-4 z-50 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur border border-teal-200 shadow-lg rounded-full px-3 py-1.5 text-xs font-bold text-teal-800 hover:bg-teal-50 hover:border-teal-400 transition"
        data-testid="lang-toggle-register-top"
        title={t("language")}
        aria-label={t("language")}
      >
        <Globe size={13} /> {lang === "ar" ? "English" : "عربي"}
      </button>
      <div className="w-full max-w-md animate-fade-up">
        <Brand variant="hero" title={t("create_account_title")} subtitle={t("create_account_subtitle")} className="mb-5 sm:mb-6" />

        <div className="bg-white rounded-2xl card-shadow p-5 sm:p-6">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="inp-label flex items-center gap-2">
                <UserCircle size={14} /> {t("full_name")}
              </label>
              <input type="text" value={form.full_name} onChange={set("full_name")} placeholder={t("full_name_placeholder")}
                className="inp" data-testid="register-fullname-input" />
            </div>

            <div>
              <label className="inp-label flex items-center gap-2">
                <User size={14} /> {t("username")} <span className="text-rose-500">*</span>
              </label>
              <input type="text" value={form.username} onChange={set("username")} placeholder="e.g. ahmad_m"
                style={{ direction: "ltr" }} className="inp" autoComplete="username" data-testid="register-username-input" />
              <p className="text-[11px] text-gray-400 mt-1">{t("username_hint")}</p>
            </div>

            <div>
              <label className="inp-label flex items-center gap-2">
                <Mail size={14} /> {t("email")} <span className="text-rose-500">*</span>
              </label>
              <input type="email" value={form.email} onChange={set("email")} placeholder="name@example.com"
                style={{ direction: "ltr" }} className="inp" autoComplete="email" data-testid="register-email-input" />
            </div>

            <div>
              <label className="inp-label flex items-center gap-2">
                <Phone size={14} /> {t("phone")}
              </label>
              <input type="tel" value={form.phone} onChange={set("phone")} placeholder="05xxxxxxxx"
                style={{ direction: "ltr" }} className="inp" autoComplete="tel" data-testid="register-phone-input" />
            </div>

            <div>
              <label className="inp-label flex items-center gap-2">
                <Lock size={14} /> {t("password")} <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={form.password} onChange={set("password")}
                  placeholder="••••••••" style={{ direction: "ltr" }} className="inp pe-10" autoComplete="new-password" data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute top-1/2 -translate-y-1/2 end-2 text-gray-400 hover:text-gray-600 p-1" tabIndex={-1}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {/* Strength bar */}
              <div className="mt-2 grid grid-cols-4 gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i}
                    className={`h-1 rounded ${i < score ? (score >= 4 ? "bg-emerald-500" : score >= 3 ? "bg-amber-500" : "bg-rose-400") : "bg-gray-200"}`} />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-1 mt-2">
                <Check ok={checks.length} label={t("pw_check_length")} testid="pw-check-length" />
                <Check ok={checks.letter} label={t("pw_check_letter")} testid="pw-check-letter" />
                <Check ok={checks.digit} label={t("pw_check_digit")} testid="pw-check-digit" />
                <Check ok={checks.symbol} label={t("pw_check_symbol")} testid="pw-check-symbol" />
              </div>
            </div>

            <div>
              <label className="inp-label flex items-center gap-2">
                <Lock size={14} /> {t("password_confirm")} <span className="text-rose-500">*</span>
              </label>
              <input type={showPw ? "text" : "password"} value={form.password_confirm} onChange={set("password_confirm")}
                placeholder="••••••••" style={{ direction: "ltr" }} className="inp" data-testid="register-password-confirm-input" />
              {form.password_confirm && (
                <p className={`text-[11px] mt-1 ${pwMatches ? "text-emerald-700" : "text-rose-600"}`} data-testid="pw-match-msg">
                  {pwMatches ? t("pw_match_ok") : t("pw_match_fail")}
                </p>
              )}
            </div>

            {error && (
              <div className="status-expired pill px-3 py-2 w-full justify-center" data-testid="register-error">{error}</div>
            )}

            <button type="submit" disabled={!canSubmit}
              className="btn btn-primary w-full justify-center py-3" data-testid="register-submit-button">
              {loading ? t("processing") : t("create_account")}
            </button>
          </form>

          <div className="text-center mt-5 text-sm text-gray-600">
            {t("have_account")}{" "}
            <Link to="/login" className="text-teal-700 font-semibold hover:underline" data-testid="goto-login-link">
              {t("login")}
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-5 flex items-center justify-center gap-1">
          <Heart size={12} className="text-rose-400" /> {t("footer_tagline")}
        </p>

        <div className="flex justify-center mt-5">
          <button onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="btn btn-secondary" data-testid="lang-toggle-register" title={t("language")}>
            <Globe size={14} /> {lang === "ar" ? "English" : "عربي"}
          </button>
        </div>
      </div>
    </div>
  );
}
